import {
  PlatformType,
  IOS14Type,
  CampaignFormData,
  AdgroupFormData,
  AppItem,
  IMAGE_X_IMG_TEMPLATE,
  AdgroupListItem,
  AdgroupConfig,
  defaultImageLoadFailedPicture,
} from '@tt4b/ttam-enums';
import { computed, Ref, ComputedRef } from '@vue/composition-api';
import { DataItem, SAN_STATUS } from './types';
import {
  useStdObjectiveType,
  useStdAdgroupIs,
  useDCAdgroup,
  useStore,
  StateEnum,
  useStdAdgroupCbo,
  useAllowlist,
} from '@tt4b/ttam-hooks';
import { getImgSrcByImageX } from '@tt4b/ttam-utils';
import AppleIcon from '@tt4b/ttam-assets/src/svgs/apple.svg?url';
import AndroidIcon from '@tt4b/ttam-assets/src/svgs/android.svg?url';

const AppStatusValue = {
  Permitted: 100,
  OwnershipVerifiedViaTTAppID: 101,
  Whitelisted: 200,
  Probated: 300,
  BlockedConflictAppAccount: 400,
  BlockedUnsupportedMMP: 401,
};

export function useDataSource(opt: { adgroupData: Ref<AdgroupFormData>; campaignData: Ref<CampaignFormData> }) {
  const { state, getStore } = useStore();
  const appList = computed(() => state.promotionAppList.data.value ?? []);
  const { isLoading: loading, run } = getStore(StateEnum.promotionAppList);
  const { isCBOVO } = useStdAdgroupCbo({ adgroupFormData: opt.adgroupData, campaignFormData: opt.campaignData });
  const { isAppInstall, isDpa, isConversion } = useStdObjectiveType({ campaignFormData: opt.campaignData });
  const { isEnableDisabledNonSan } = useAllowlist();

  const isSupportCBOVOAndObjective = computed(
    () => isCBOVO.value && (isAppInstall.value || isDpa.value || isConversion.value),
  );
  const data: ComputedRef<DataItem[]> = computed(() => {
    const list = appList.value.map(i => ({
      ...i,
      name: i.AppName || '- -',
      icon: i.Platform === PlatformType.IOS ? AppleIcon : AndroidIcon,
      img:
        getImgSrcByImageX(i.Icon.web_uri, IMAGE_X_IMG_TEMPLATE.COMPRESS_IMAGE, process.env.TTAM_BOE_IMAGEX_DOMAIN) ||
        defaultImageLoadFailedPicture,
      desc: `ID：${i.AppId}`,
      // App如果没有开启SAN则禁止选择，全量后，再下掉 isEnableDisabledNonSan 白名单
      disabled: i.SanStatus === SAN_STATUS.NotEnableOnMMP && isEnableDisabledNonSan.value,
    }));
    return isSupportCBOVOAndObjective.value ? list.filter(i => i.Platform === PlatformType.ANDROID) : list;
  });

  const isEmpty = computed(() => data.value.length === 0);

  return {
    dataSource: data,
    originalData: appList,
    isEmpty,
    loading,
    loadData: run,
  };
}

export function usePropsDisabled(opt: {
  dcAdgroupList: Ref<AdgroupListItem[]>;
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  adgroupConfig: Ref<AdgroupConfig>;
}) {
  const { isDCHasAdgroup } = useDCAdgroup({
    campaignFormData: opt.campaignData,
    dcAdgroupList: opt.dcAdgroupList,
    adgroupFormData: opt.adgroupData,
  });

  return {
    disabled: computed(() => isDCHasAdgroup.value || opt.adgroupConfig.value.disableAppSelect),
  };
}

// tips
export function usePropsRetargetingInfo(opt: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { isConversion } = useStdObjectiveType({ campaignFormData: opt.campaignData });
  const { isAppDownload } = useStdAdgroupIs({ adgroupFormData: opt.adgroupData, campaignFormData: opt.campaignData });

  return {
    isShowInfo: computed(() => isConversion.value && isAppDownload.value),
  };
}

// ios14 tips
export function usePropsIOS14Visible(opt: { adgroupData: Ref<AdgroupFormData>; item: Ref<AppItem> }) {
  return {
    ios14TipsVisible: computed(
      () =>
        opt.adgroupData.value.targetDeviceVersion === IOS14Type.ABOVE &&
        [
          AppStatusValue.Probated,
          AppStatusValue.BlockedConflictAppAccount,
          AppStatusValue.BlockedUnsupportedMMP,
        ].includes(opt.item.value?.AccountAppVerifiedState),
    ),
  };
}
